import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

const repriceCart = (items) => {
  const distinctTextures = new Map();
  items.forEach(item => {
    if (!distinctTextures.has(item.productId)) {
      distinctTextures.set(item.productId, new Set());
    }
    if (item.texture && item.texture !== 'default') {
      distinctTextures.get(item.productId).add(item.texture);
    }
  });

  return items.map(item => {
    const basePrice = roundCurrency(item.basePrice ?? item.price);
    const bundleQuantity = Math.max(2, Number.parseInt(item.bundleQuantity, 10) || 3);
    const bundleDiscountPercent = Math.min(100, Math.max(0, parseFloat(item.bundleDiscountPercent) || 0));
    const bundleDiscountApplied = Boolean(item.bundleEnabled)
      && bundleDiscountPercent > 0
      && (distinctTextures.get(item.productId)?.size || 0) >= bundleQuantity;
    const price = bundleDiscountApplied
      ? roundCurrency(basePrice * (1 - bundleDiscountPercent / 100))
      : basePrice;

    return {
      ...item,
      basePrice,
      price,
      bundleQuantity,
      bundleDiscountPercent,
      bundleDiscountApplied
    };
  });
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('vague_cart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem('vague_cart', JSON.stringify(cart));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  }, [cart, isLoaded]);

  useEffect(() => {
    let isUpdatingFromStorage = false;

    const handleStorageChange = (e) => {
      if (isUpdatingFromStorage) {
        return;
      }

      if (e.key === 'vague_cart') {
        if (e.newValue) {
          try {
            const newCart = JSON.parse(e.newValue);
            setCart(prevCart => {
              const prevCartStr = JSON.stringify(prevCart);
              if (prevCartStr !== e.newValue) {
                return newCart;
              }
              return prevCart;
            });
          } catch (error) {
            console.error('Error parsing cart from storage event:', error);
          }
        } else {
          setCart([]);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const getEffectivePrice = (product) => {
    if (!product) return 0;
    const discounted = product.discountedPrice ?? product.discounted_price;
    if (discounted !== null && discounted !== undefined && discounted !== '') {
      return parseFloat(discounted) || 0;
    }
    return parseFloat(product.price) || 0;
  };

  const addItemsToCart = (product, texturesToAdd) => {
    if (product.hidden) {
      throw new Error('This product is no longer available.');
    }
    const hasTextures = product.textures && product.textures.length > 0;
    if (hasTextures && texturesToAdd.some(texture => !texture || !product.textures.includes(texture))) {
      throw new Error('Please select a Color / Style for this product');
    }

    setCart((prevCart) => {
      const cartCopy = [...prevCart];
      texturesToAdd.forEach(texture => {
        const normalizedTexture = texture || 'default';
        const existingItemIndex = cartCopy.findIndex(
          (item) => item.productId === product.id && item.texture === normalizedTexture
        );
        const productImage = (normalizedTexture !== 'default' && product.images?.[normalizedTexture])
          ? product.images[normalizedTexture]
          : (product.images?.default || '');

        if (existingItemIndex >= 0) {
          cartCopy[existingItemIndex] = {
            ...cartCopy[existingItemIndex],
            productName: product.name,
            productImage,
            basePrice: getEffectivePrice(product),
            bundleEnabled: Boolean(product.bundleEnabled),
            bundleQuantity: Number(product.bundleQuantity) || 3,
            bundleDiscountPercent: parseFloat(product.bundleDiscountPercent) || 0,
            quantity: cartCopy[existingItemIndex].quantity + 1
          };
        } else {
          cartCopy.push({
            productId: product.id,
            productName: product.name,
            productImage: productImage,
            texture: normalizedTexture,
            basePrice: getEffectivePrice(product),
            price: getEffectivePrice(product),
            bundleEnabled: Boolean(product.bundleEnabled),
            bundleQuantity: Number(product.bundleQuantity) || 3,
            bundleDiscountPercent: parseFloat(product.bundleDiscountPercent) || 0,
            quantity: 1,
          });
        }
      });

      return repriceCart(cartCopy);
    });
  };

  const addToCart = (product, texture) => {
    addItemsToCart(product, [texture]);
  };

  const addTextureSetToCart = (product, selectedTextures) => {
    const uniqueTextures = [...new Set(selectedTextures)].filter(Boolean);
    const requiredQuantity = Math.max(2, Number.parseInt(product.bundleQuantity, 10) || 3);
    if (!product.bundleEnabled || uniqueTextures.length !== requiredQuantity) {
      throw new Error(`Select exactly ${requiredQuantity} different Color / Style options for this set`);
    }
    addItemsToCart(product, uniqueTextures);
  };

  const removeFromCart = (productId, texture) => {
    setCart((prevCart) =>
      repriceCart(prevCart.filter(
        (item) => !(item.productId === productId && item.texture === texture)
      ))
    );
  };

  const updateQuantity = (productId, texture, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, texture);
      return;
    }

    setCart((prevCart) =>
      repriceCart(prevCart.map((item) =>
        item.productId === productId && item.texture === texture
          ? { ...item, quantity: Math.max(1, Math.floor(quantity)) }
          : item
      ))
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.price) || 0) * item.quantity, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const validateAndUpdateCart = (products, textures = []) => {
    const productsMap = new Map(products.map(p => [p.id, p]));
    const texturesMap = new Set(textures.map(t => typeof t === 'string' ? t : t.id));
    const updatedCart = [];
    let hasChanges = false;

    for (const item of cart) {
      const product = productsMap.get(item.productId);

      if (!product) {
        hasChanges = true;
        continue;
      }

      if (product.hidden) {
        hasChanges = true;
        continue;
      }

      const currentPrice = getEffectivePrice(product);

      const hasTextures = product.textures && product.textures.length > 0;

      let textureExists = true;
      if (hasTextures) {
        if (item.texture === 'default') {
          textureExists = false;
        } else {
          textureExists = product.textures.includes(item.texture) && texturesMap.has(item.texture);
        }
      } else {
        textureExists = item.texture === 'default';
      }

      if (!textureExists) {
        hasChanges = true;
        continue;
      }

      const productImage = (item.texture !== 'default' && product.images?.[item.texture])
        ? product.images[item.texture]
        : (product.images?.default || item.productImage);

      updatedCart.push({
        ...item,
        basePrice: currentPrice,
        productName: product.name,
        productImage: productImage,
        bundleEnabled: Boolean(product.bundleEnabled),
        bundleQuantity: Number(product.bundleQuantity) || 3,
        bundleDiscountPercent: parseFloat(product.bundleDiscountPercent) || 0,
      });
    }

    const repricedCart = repriceCart(updatedCart);
    if (JSON.stringify(repricedCart) !== JSON.stringify(cart)) {
      hasChanges = true;
      setCart(repricedCart);
    }

    return hasChanges;
  };

  const value = {
    cart,
    addToCart,
    addTextureSetToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount,
    validateAndUpdateCart,
    isLoaded,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

